import { useState } from "react";
import Dropzone, { useDropzone } from "react-dropzone";
import { Media } from "../../interfaces/media";
import { createMedia } from "../../services/media";
import { t } from "i18next";
import axios from "axios";

export interface UploaderProps {
    media: Media[];
    save: CallableFunction;
    title: string;
}

export default function Uploader(props: UploaderProps) {
    const userId = localStorage.getItem('userId');
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{name: string, progress: number, status: 'uploading' | 'completed' | 'error'}[]>([]);

    const {getRootProps, getInputProps} = useDropzone({
        multiple: true,
        accept: {
            'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'],
            'audio/*': ['.mp3'], 'video/mp4': ['.mp4'],
            'application/pdf': ['.pdf'],
            'application/vnd.ms-powerpoint': ['.ppt'],
            'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
            'application/vnd.apple.keynote': ['.key'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.apple.numbers': ['.numbers'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'application/vnd.apple.pages': ['.pages'],
            'application/zip': ['.zip'], 'application/x-rar': ['.rar'], 'text/plain': ['.txt']
        }
    });

    const uploadFiles = async (f: any[]) =>  {
        if (f.length > 0) {
            // Límite de tamaño: 5MB para imágenes
            const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB en bytes
            const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

            // Validar tamaño de archivos antes de subir
            const filesToUpload: File[] = [];
            const rejectedFiles: {file: File, reason: string}[] = [];

            for (const file of f) {
                if (IMAGE_TYPES.includes(file.type) && file.size > MAX_IMAGE_SIZE) {
                    rejectedFiles.push({
                        file,
                        reason: t("Image too large") + ` (${(file.size / (1024 * 1024)).toFixed(2)}MB). ${t("Maximum size for images is 5MB")}`
                    });
                } else {
                    filesToUpload.push(file);
                }
            }

            // Mostrar archivos rechazados
            if (rejectedFiles.length > 0) {
                const messages = rejectedFiles.map(rf => `• ${rf.file.name}: ${rf.reason}`).join('\n');
                alert(t("Some files were not uploaded") + ':\n\n' + messages);
            }

            // Si no hay archivos válidos, salir
            if (filesToUpload.length === 0) {
                return;
            }

            setUploadingFiles(true);

            // Inicializar el progreso para cada archivo
            const initialProgress = filesToUpload.map(file => ({
                name: file.name,
                progress: 0,
                status: 'uploading' as const
            }));
            setUploadProgress(initialProgress);

            // Subir archivos uno por uno
            const uploadedMedia: Media[] = [];
            for (let i = 0; i < filesToUpload.length; i++) {
                const file = filesToUpload[i];
                const formData = new FormData();
                formData.append('files[]', file);

                // Variable para controlar la frecuencia de actualizaciones (1 segundo)
                let lastUpdate = 0;

                try {
                    const response = await axios.post(
                        process.env.REACT_APP_API_BASE_URL + '/wp-json/badminton/ws/v1/media',
                        formData,
                        {
                            headers: {
                                'Authorization': 'Bearer ' + localStorage.getItem('token'),
                                'Content-Type': 'multipart/form-data'
                            },
                            timeout: 600000, // 10 minutos de timeout
                            maxContentLength: Infinity,
                            maxBodyLength: Infinity,
                            onUploadProgress: (progressEvent) => {
                                const now = Date.now();
                                const percentCompleted = progressEvent.total
                                    ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
                                    : 0;

                                // Solo actualizar cada segundo (1000ms) o al completar (100%)
                                if (now - lastUpdate >= 1000 || percentCompleted === 100) {
                                    lastUpdate = now;
                                    setUploadProgress(prev => prev.map((item, index) =>
                                        index === i ? { ...item, progress: percentCompleted } : item
                                    ));
                                }
                            }
                        }
                    );

                    if (response.data !== undefined && response.data.length > 0) {
                        uploadedMedia.push(...response.data);
                        // Marcar como completado
                        setUploadProgress(prev => prev.map((item, index) =>
                            index === i ? { ...item, status: 'completed' as const, progress: 100 } : item
                        ));
                    } else {
                        // Marcar como error
                        setUploadProgress(prev => prev.map((item, index) =>
                            index === i ? { ...item, status: 'error' as const } : item
                        ));
                    }
                } catch (err) {
                    console.log(err);
                    // Marcar como error
                    setUploadProgress(prev => prev.map((item, index) =>
                        index === i ? { ...item, status: 'error' as const } : item
                    ));
                }
            }

            // Actualizar la lista de media
            if (uploadedMedia.length > 0) {
                const newMedia = [...props.media, ...uploadedMedia];
                props.save(newMedia);
            }

            // Esperar 2 segundos antes de ocultar el progreso
            setTimeout(() => {
                setUploadingFiles(false);
                setUploadProgress([]);
            }, 2000);
        }
    }

    const deleteMedia = (event: any) => {
        event.preventDefault();
        const newMedia = props.media.filter((m: Media, index) => index != event.target.dataset.position);
        props.save(newMedia);
    }

    return (
        <div className="field field-media">
            <div className="label">{ props.title }</div>
            { props.media.length > 0 &&
                <div className="media-items-list">
                    { props.media.map((m: Media, index) => {
                        switch (m.mimetype) {
                            case 'image/jpeg':
                            case 'image/png':
                                return (<div className={ 'image media-item media-' + m.id } key={ 'media-item-' + m.id }>
                                        <img src={ m.src } alt={ m.name } height="50" />
                                        {/*<a className="icon" href={ m.url } target="_blank" title={ m.name }></a>
                                        <a className="name" href={ m.url } target="_blank" title={ m.name }>{ m.name }</a>
                                        */}
                                        <button className="delete" onClick={ deleteMedia } data-position={ index } />
                                    </div>);
                                break;
                            case 'video/mp4':
                                return (<div className={ 'video media-item media-' + m.id } key={ 'media-item-' + m.id }>
                                    <a className="icon" href={ m.url } target="_blank" title={ m.name }></a>
                                    <a className="name" href={ m.url } target="_blank" title={ m.name }>{ m.name }</a>
                                    <button className="delete" onClick={ deleteMedia } data-position={ index } />
                                </div>);
                                break;
                            case 'audio/mpeg':
                                return (<div className={ 'audio media-item media-' + m.id } key={ 'media-item-' + m.id }>
                                    <a className="icon" href={ m.url } target="_blank" title={ m.name }></a>
                                    <a className="name" href={ m.url } target="_blank" title={ m.name }>{ m.name }</a>
                                    <button className="delete" onClick={ deleteMedia } data-position={ index } />
                                </div>);
                                break;
                            case 'application/pdf':
                                return (<div className={ 'pdf media-item media-' + m.id } key={ 'media-item-' + m.id }>
                                    <a className="icon" href={ m.url } target="_blank" title={ m.name }></a>
                                    <a className="name" href={ m.url } target="_blank" title={ m.name }>{ m.name }</a>
                                    <button className="delete" onClick={ deleteMedia } data-position={ index } />
                                </div>);
                                break;
                            case 'application/vnd.ms-powerpoint':
                            case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
                            case 'application/vnd.apple.keynote':
                                return (<div className={ 'presentation media-item media-' + m.id } key={ 'media-item-' + m.id }>
                                    <a className="icon" href={ m.url } target="_blank" title={ m.name }></a>
                                    <a className="name" href={ m.url } target="_blank" title={ m.name }>{ m.name }</a>
                                    <button className="delete" onClick={ deleteMedia } data-position={ index } />
                                </div>);
                                break;
                            case 'application/vnd.ms-excel':
                            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                            case 'application/vnd.apple.numbers':
                                return (<div className={ 'spreadsheet media-item media-' + m.id } key={ 'media-item-' + m.id }>
                                    <a className="icon" href={ m.url } target="_blank" title={ m.name }></a>
                                    <a className="name" href={ m.url } target="_blank" title={ m.name }>{ m.name }</a>
                                    <button className="delete" onClick={ deleteMedia } data-position={ index } />
                                </div>);
                                break;
                            case 'application/msword':
                            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                            case 'application/vnd.apple.pages':
                                return (<div className={ 'document media-item media-' + m.id } key={ 'media-item-' + m.id }>
                                    <a className="icon" href={ m.url } target="_blank" title={ m.name }></a>
                                    <a className="name" href={ m.url } target="_blank" title={ m.name }>{ m.name }</a>
                                    <button className="delete" onClick={ deleteMedia } data-position={ index } />
                                </div>);
                                break;
                            case 'application/zip':
                            case 'application/x-rar':
                                return (<div className={ 'compress media-item media-' + m.id } key={ 'media-item-' + m.id }>
                                    <a className="icon" href={ m.url } target="_blank" title={ m.name }></a>
                                    <a className="name" href={ m.url } target="_blank" title={ m.name }>{ m.name }</a>
                                    <button className="delete" onClick={ deleteMedia } data-position={ index } />
                                </div>);
                                break;
                            case 'text/plain':
                                return (<div className={ 'text media-item media-' + m.id } key={ 'media-item-' + m.id }>
                                    <a className="icon" href={ m.url } target="_blank" title={ m.name }></a>
                                    <a className="name" href={ m.url } target="_blank" title={ m.name }>{ m.name }</a>
                                    <button className="delete" onClick={ deleteMedia } data-position={ index } />
                                </div>);
                                break;
                        }})
                    }
                </div>
            }
            {/*files.length > 0 &&
                <div className="uploaded-files">
                    { files.map((f: any) => {
                        return (
                            <div className="image" style={ { backgroundImage: 'url(' + 'https://srv.taskgen.eu/' + f + ')' } }></div>
                        )
                    }) }
                </div>
            */}
            {<Dropzone onDrop={acceptedFiles => uploadFiles(acceptedFiles)}>
                {({getRootProps, getInputProps}) => (
                    <section>
                    <div {...getRootProps()}>
                        <input name="files" {...getInputProps()} />
                        {/* files && files.map((f: File) => {
                            return <div className="file-item">{ f.name }</div>;
                        }) */}
                        <p>{ 'Arrastra y suelta archivos aquí' }</p>

                        {uploadingFiles && (
                            <div className="upload-overlay">
                                <h5>{t("Uploading files...")}</h5>
                                {uploadProgress.map((file, index) => (
                                    <div key={index} className="file-upload-item">
                                        <div className="file-info">
                                            <span>{file.name}</span>
                                            {file.status === 'completed' && <i className="bi bi-check-circle-fill text-success"></i>}
                                            {file.status === 'error' && <i className="bi bi-x-circle-fill text-danger"></i>}
                                        </div>
                                        <div className="progress">
                                            <div
                                                className={`progress-bar ${file.status === 'completed' ? 'bg-success' : file.status === 'error' ? 'bg-danger' : ''}`}
                                                role="progressbar"
                                                style={{width: `${file.progress}%`}}
                                                aria-valuenow={file.progress}
                                                aria-valuemin={0}
                                                aria-valuemax={100}
                                            >
                                                {file.progress}%
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    </section>
                )}
                </Dropzone> }
        </div>
    );
}